import React, {useEffect, useMemo, useState, useCallback, useRef} from 'react';
import s from './subscriptionsAccounting.module.scss';
import Header from "../../../components/Partials/Header/Header";
import SvgSelector from "../../../components/SvgSelector/SvgSelector";
import MultiSelect from "../../../components/Selects/MultiSelect/MultiSelect";
import {
    params,
    subscriptionsAccountingTableOrderingList,
} from "../../../utils/vars/params";
import {
    INVALID_PAGE_ERROR,
    snackbar_error,
    snackbar_success,
} from "../../../utils/vars/staticVariables";
import {cleanSearchParams, hasNonEmptyParams} from "../../../utils/helpers/cleanFilters";
import EmptyList from "../../../components/EmptyList/EmptyList";
import ParamsSort from "../../../components/ParamsSort/ParamsSort";
import CustomSkeleton from "../../../components/UiKit/CustomSkeleton/CustomSkeleton";
import {PageSizeDropdown} from "../../../components/UiKit/PageSizeDropdown/PageSizeDropdown";
import Pagination from "../../../components/Pagination/Pagination";
import {
    date_from,
    date_to, email,
    page,
    subscriptions, teams,
} from "../../Authorized/Statistics/IOS/IOSClicksStats/IOSClicksStats";
import {useDispatch, useSelector} from "react-redux";
import {fetchUsersList, globalDataSelector} from "../../../redux/slices/globalDataSlice";
import useSetSearchParams from "../../../hooks/useSetSearchParams";
import {useSearchParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {getDefaultParams} from "../../../api/helpers/scripts";
import SubscriptionsAccountingTr from "./SubscriptionsAccountingTr/SubscriptionsAccountingTr";
import useCustomSnackbar from "../../../hooks/useCustomSnackbar";
import {onSetPageSize} from "../../../utils/helpers/statisticsHelper";
import useGetSearchParams from "../../../hooks/useGetSearchParams";
import {useSearchParamsOptions, useSelectOptions} from "../../../utils/helpers/useSelectHelpers";
import {
    getAllVipBotTeams,
    getSubscriptionsAccountingData,
    subscriptionsAccountingSelector,
    subscriptionsAccountingSetExtraMark,
    subscriptionsAccountingSetExtraMarkDetail,
    subscriptionsAccountingSetNote, updateSubscriptionAccountingData,
} from "../../../redux/slices/subscriptionsAccountingSlice";
import {subscriptionSelector} from "../../../redux/slices/subscriptionSlice";

const SubscriptionsAccounting = () => {
    const {isCollapsedSidebar, pageSizeValue, usersList} = useSelector(globalDataSelector);
    const {
        subscriptionsAccountingData,
        subscriptionsAccountingDataPending,
        subscriptionsAccountingRecordsCount,
        allVipBotTeamsArray
    } = useSelector(subscriptionsAccountingSelector);

    const {subscriptionsList} = useSelector(subscriptionSelector);

    const dispatch = useDispatch();
    const showSnackbar = useCustomSnackbar();
    const [searchParams, setSearchParams] = useSearchParams();
    const updateSearchParams = useSetSearchParams(setSearchParams);
    const {t} = useTranslation();

    const updateSearchParamsRef = useRef(updateSearchParams);
    useEffect(() => {
        updateSearchParamsRef.current = updateSearchParams;
    }, [updateSearchParams]);

    const [selectedSubscriptions, selectedTeams] = useSearchParamsOptions(searchParams, [subscriptions, teams]);
    const subscriptionsListOptions = useSelectOptions(subscriptionsList);

    const {
        page: pageValue,
        hidden_columns: hiddenColumnsValue,
        email: emailParam,
    } = useGetSearchParams();

    const selectedEmail = useMemo(() => {
        return emailParam ? {value: emailParam, label: emailParam} : null;
    }, [emailParam]);

    const columnOptions = subscriptionsAccountingTableOrderingList.map(({name}) => ({
        value: name,
        label: t(name),
    }));

    const teamsOptions = useMemo(() => {
        return allVipBotTeamsArray.map(team => ({
            value: team,
            label: team,
        }));
    }, [allVipBotTeamsArray]);

    const emailsListOptions = useSelectOptions(usersList, "email", "email");

    const [hiddenColumns, setHiddenColumns] = useState(
        hiddenColumnsValue ? hiddenColumnsValue.split(',') : []
    );

    const filteredParamsString = useMemo(() => {
        const copy = new URLSearchParams(searchParams);
        copy.delete('hidden_columns');
        return copy.toString();
    }, [searchParams]);

    const reFetch = useCallback(() => {
        const paramsCopy = new URLSearchParams(filteredParamsString);
        const promise = dispatch(
            getSubscriptionsAccountingData(
                getDefaultParams({
                    searchParams: paramsCopy,
                    defaultPageSize: pageSizeValue,
                })
            )
        );
        promise.unwrap().catch(err => {
            if (err?.message === INVALID_PAGE_ERROR) {
                updateSearchParamsRef.current(page, 1);
            }
        });
        return promise;
    }, [dispatch, filteredParamsString, pageSizeValue]);

    useEffect(() => {
        const p = reFetch();
        return () => {
            p.abort();
        };
    }, [reFetch]);

    useEffect(() => {
        dispatch(getAllVipBotTeams())
        dispatch(fetchUsersList())
    }, [dispatch]);

    const editSubscriptionNote = useCallback(async (form, setActive) => {
        try {
            const {status} = await dispatch(subscriptionsAccountingSetNote(form)).unwrap();

            if (status === 200) {
                dispatch(updateSubscriptionAccountingData({
                    subscription_service_id: form.subscription_service_id,
                    value: form.note,
                    key: 'note'
                }))
                setActive(false);
                showSnackbar(t("note_has_been_set"), snackbar_success);
            } else {
                showSnackbar(t("failed_to_set_note"), snackbar_error);
            }
        } catch (error) {
            console.error("Error updating subscription note:", error);
            showSnackbar(t("failed_to_set_note"), snackbar_error);
        }
    }, [dispatch, showSnackbar, t]);

    const setExtraMarkHandler = useCallback(async (id, mark) => {
        let edited = '';

        if (mark !== 'clear_button') edited = mark;

        try {
            const {status} = await dispatch(
                subscriptionsAccountingSetExtraMark({subscription_service_id: id, extra_mark: edited})
            ).unwrap();

            if (status === 200) {

                dispatch(updateSubscriptionAccountingData({
                    subscription_service_id: id,
                    value: edited,
                    key: 'extra_mark'
                }))
                showSnackbar(t('extra_status_set'), snackbar_success);
            } else {
                showSnackbar(t('failed_to_set_extra_status'), snackbar_error);
            }
        } catch {
            showSnackbar(t('failed_to_set_extra_status'), snackbar_error);
        }
    }, [dispatch, showSnackbar, t]);

    const setExtraMarkDetailHandler = useCallback(async (id, extra_mark_detail, setActive) => {

        try {
            const {status} = await dispatch(
                subscriptionsAccountingSetExtraMarkDetail({subscription_service_id: id, extra_mark_detail})
            ).unwrap();

            if (status === 200) {

                dispatch(updateSubscriptionAccountingData({
                    subscription_service_id: id,
                    value: extra_mark_detail,
                    key: 'extra_mark_detail'
                }))
                setActive(false)
                showSnackbar(t('extra_status_description_set'), snackbar_success);
            } else {
                showSnackbar(t('failed_to_set_extra_status_description'), snackbar_error);
            }
        } catch {
            showSnackbar(t('failed_to_set_extra_status_description'), snackbar_error);
        }
    }, [dispatch, showSnackbar, t]);

    const cleanFilters = () => {
        cleanSearchParams(searchParams, setSearchParams)
        setHiddenColumns([])
    }

    const visibleColumns = subscriptionsAccountingTableOrderingList.filter(
        col => !hiddenColumns.includes(col.name)
    );

    const thead = (
        <thead>
        <tr>
            {visibleColumns.map(({ordering, name}) => (
                <th key={name}>
                    <div className={s.headerContent}>
                        {t(name)}
                        {ordering && (
                            <ParamsSort
                                sortBy={ordering}
                                searchParams={searchParams}
                                setSearchParams={setSearchParams}
                            />
                        )}
                    </div>
                </th>
            ))}
        </tr>
        </thead>
    );

    return (
        <div
            className={`${s.subscriptionAccountingWrapper} ${isCollapsedSidebar === "true" ? s.collapsedSidebar : ""}`}>
            <Header/>
            <div className={s.pageContent}>
                <div className={s.filtersWrapper}>
                    <div className={s.filters}>
                        <div className={s.selectWrapper}>
                            <div className={s.selectIconWrapper}><SvgSelector id="subscriptions-icon"/></div>
                            <MultiSelect
                                isClearable
                                isMulti
                                options={[
                                    ...subscriptionsListOptions,
                                    {value: 'Installation fee', label: 'Installation fee'}
                                ]}
                                value={selectedSubscriptions}
                                setSelectedOptions={vals =>
                                    updateSearchParams(subscriptions, vals.map(v => v.value).join(','))
                                }
                                placeholder={t("subscription_level")}
                            />
                        </div>
                        <div className={s.selectWrapper}>
                            <div className={s.selectIconWrapper}><SvgSelector id="teams-icon"/></div>
                            <MultiSelect
                                isClearable
                                isMulti
                                options={teamsOptions}
                                value={selectedTeams}
                                setSelectedOptions={vals =>
                                    updateSearchParams(teams, vals.map(v => v.value).join(','))
                                }
                                placeholder={t("team")}
                            />
                        </div>
                        <div className={s.selectWrapper}>
                            <div className={s.selectIconWrapper}>
                                <SvgSelector id="user-select-icon"/>
                            </div>
                            <MultiSelect
                                isClearable={true}
                                value={selectedEmail}
                                options={emailsListOptions}
                                setSelectedOptions={(option) => {
                                    if (option) {
                                        const {value} = option;
                                        updateSearchParams(email, value);
                                    } else {
                                        updateSearchParams(email, "", true);
                                    }
                                }}
                                placeholder={t("user")}
                            />
                        </div>
                        <div className={s.selectWrapper}>
                            <div className={s.selectIconWrapper}><SvgSelector id="checklist-icon"/></div>
                            <MultiSelect
                                isMulti
                                isClearable
                                options={columnOptions}
                                value={columnOptions.filter(opt => hiddenColumns.includes(opt.value))}
                                setSelectedOptions={vals => {
                                    const arr = vals.map(v => v.value);
                                    setHiddenColumns(arr);
                                    updateSearchParams("hidden_columns", arr.join(','));
                                }}
                                placeholder={t("hidden_columns")}
                            />
                        </div>
                        {hasNonEmptyParams(searchParams) && (
                            <div className={s.cleanBtnContainer}>
                                <button className={s.blueBorderBtn} onClick={cleanFilters}>
                                    {t("clear_button")}
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                <div className={s.pageInnerContent}>
                    {subscriptionsAccountingRecordsCount === 0 && !subscriptionsAccountingDataPending ? (
                        <EmptyList/>
                    ) : subscriptionsAccountingDataPending ? (
                        <section className={s.body}>
                            <table>{thead}</table>
                            <div className={s.skeletonWrapper}>
                                <CustomSkeleton styles={s.skeletonTable}/>
                            </div>
                        </section>
                    ) : (
                        <section className={s.body}>
                            <table>
                                {thead}
                                <tbody>
                                {subscriptionsAccountingData.map(item => (
                                    <SubscriptionsAccountingTr
                                        key={item.email}
                                        {...item}
                                        visibleColumns={visibleColumns}
                                        editSubscriptionNote={editSubscriptionNote}
                                        setExtraMarkHandler={setExtraMarkHandler}
                                        setExtraMarkDetailHandler={setExtraMarkDetailHandler}
                                    />
                                ))}
                                </tbody>
                            </table>
                        </section>
                    )}
                </div>

                <div
                    className={`${s.paginationContainerStatistics} ${isCollapsedSidebar === "true" ? s.collapsed : ""}`}>
                    <div className={s.countPageSizeContainer}>
                        <div className={s.newRecordsCount}>
                            {t("total")}: {!subscriptionsAccountingDataPending &&
                            <span>{subscriptionsAccountingRecordsCount}</span>}
                        </div>
                        <div className={s.pageSizeDropdownContainer}>
                            <PageSizeDropdown setSize={size => onSetPageSize(dispatch, size)} size={pageSizeValue}/>
                        </div>
                    </div>
                    {subscriptionsAccountingRecordsCount > pageSizeValue && (
                        <Pagination
                            currentPage={parseInt(pageValue) || 1}
                            setCurrentPage={p => updateSearchParams(params.page, p)}
                            total={subscriptionsAccountingRecordsCount}
                            limit={pageSizeValue}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default SubscriptionsAccounting;
